import { useCallback, useEffect, useState } from "react";

import { api } from "../../services/api";
import { guessCurrency } from "../../services/i18ln/currency";

const browserLanguages = () =>
  typeof navigator === "undefined" ? [] : navigator.languages?.length ? navigator.languages : [navigator.language];

// The account's currency once read: every screen after the first has it at once
let known: string | undefined;

/** On sign out: the next account reads its own (it doesn't start with this one's). */
export const forgetAccountCurrency = () => {
  known = undefined;
};

/**
 * The account's currency (ISO 4217): the one chosen in the Account, else the browser's guess (not saved: it's
 * only fixed when the person chooses). `loaded` once the account has answered (or couldn't). `save` fixes a choice.
 */
export const useAccountCurrency = () => {
  const [currency, setCurrency] = useState<string>(() => known ?? guessCurrency(browserLanguages()));
  const [chosen, setChosen] = useState<boolean>(false);
  const [loaded, setLoaded] = useState(() => known !== undefined);

  useEffect(() => {
    let current = true;
    api
      .getUser()
      .then(user => {
        if (!current) return;
        setChosen(!!user.currency);
        known = user.currency || guessCurrency(browserLanguages(), user.lang);
        setCurrency(known);
        setLoaded(true);
      })
      // no answer: the guess stays
      .catch(() => current && setLoaded(true));
    return () => {
      current = false;
    };
  }, []);

  const save = useCallback(async (code: string) => {
    await api.updateCurrency(code);
    known = code;
    setCurrency(code);
    setChosen(true);
  }, []);

  return { currency, chosen, loaded, save };
};
